// Atomic controls used by the admin-only TweaksPanel. Each emits `onChange`
// with the new value; debouncing/persistence happens in useAdminTweaks.

import type { ReactNode } from 'react';

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 6,
  display: 'block',
};

const rowStyle: React.CSSProperties = {
  marginBottom: 14,
};

export function TweakSection({ label }: { label: string }) {
  return (
    <div
      style={{
        marginTop: 18,
        marginBottom: 8,
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--ink)',
        borderBottom: '1px solid var(--line)',
        paddingBottom: 4,
      }}
      data-tweak-section
    >
      {label}
    </div>
  );
}

export function TweakToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{
        ...rowStyle,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      data-tweak-control
    >
      <span style={labelStyle}>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        style={{
          width: 34,
          height: 18,
          borderRadius: 999,
          border: 'none',
          background: value ? 'var(--coral)' : 'var(--line-strong)',
          position: 'relative',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: value ? 18 : 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: 'var(--paper)',
            transition: 'left .15s ease',
          }}
        />
      </button>
    </label>
  );
}

export function TweakRadio<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: T[];
  onChange: (next: T) => void;
}) {
  return (
    <div style={rowStyle} data-tweak-control>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              border: '1px solid var(--line-strong)',
              background: opt === value ? 'var(--ink)' : 'transparent',
              color: opt === value ? 'var(--paper)' : 'var(--ink)',
              fontSize: 11,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TweakSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (next: T) => void;
}) {
  return (
    <label style={rowStyle} data-tweak-control>
      <span style={labelStyle}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        style={{
          width: '100%',
          padding: '6px 8px',
          borderRadius: 8,
          border: '1px solid var(--line-strong)',
          background: 'var(--paper)',
          color: 'var(--ink)',
          fontFamily: 'inherit',
          fontSize: 12,
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function TweakSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (next: number) => void;
}) {
  return (
    <label style={rowStyle} data-tweak-control>
      <span style={labelStyle}>
        {label}: {value.toFixed(2)}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%' }}
      />
    </label>
  );
}

export function TweakColor({
  label,
  swatches,
  selectedIdx,
  onChange,
}: {
  label: string;
  swatches: Array<[string, string, string]>;
  selectedIdx: number;
  onChange: (nextIdx: number) => void;
}) {
  return (
    <div style={rowStyle} data-tweak-control>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        {swatches.map((swatch, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i)}
            aria-label={`Palette ${i + 1}`}
            style={{
              width: 38,
              height: 22,
              borderRadius: 6,
              border: i === selectedIdx ? '2px solid var(--coral)' : '1px solid var(--line-strong)',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            {swatch.map((c, j) => (
              <span key={j} style={{ flex: 1, background: c }} />
            ))}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TweakWrapper({ children, title }: { children: ReactNode; title: string }) {
  return (
    <aside
      data-tweaks-panel
      style={{
        position: 'fixed',
        bottom: 16,
        insetInlineEnd: 16,
        zIndex: 100,
        width: 280,
        maxHeight: '80vh',
        overflowY: 'auto',
        background: 'var(--paper)',
        border: '1px solid var(--line-strong)',
        borderRadius: 16,
        boxShadow: '0 20px 60px -20px rgba(14, 27, 51, 0.3)',
        padding: 16,
        fontFamily: 'var(--font-body)',
      }}
    >
      <header
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 16,
          marginBottom: 4,
        }}
      >
        {title}
      </header>
      {children}
    </aside>
  );
}
